import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';

const emptyForm = {
  tenant_slug: '', name: '', nickname: '', phone: '', email: '',
  password: '', password_confirmation: '',
};

export default function PlayerSignup() {
  const params = new URLSearchParams(window.location.search);
  const requestedTenant = params.get('tenant') || '';
  const returnTo = params.get('return') || '';
  const [form, setForm] = useState(emptyForm);
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { registerPlayer } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    authService.playerOptions().then(({ data }) => {
      setTenants(data);
      if (requestedTenant && data.some(item => item.slug === requestedTenant))
        setForm(current => ({ ...current, tenant_slug: requestedTenant }));
      else if (data.length === 1) setForm(current => ({ ...current, tenant_slug: data[0].slug }));
    }).catch(() => setError('Não foi possível carregar as unidades. Tente novamente.'));
  }, []);

  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await registerPlayer(form);
      const safeReturn = returnTo.startsWith(`/cliente/${data.tenant_slug}`) ? returnTo : `/cliente/${data.tenant_slug}`;
      navigate(safeReturn);
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível criar a conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img src="/logo.png" alt="Arena Cyber Café" style={styles.logo} />
        <h1 style={styles.title}>Criar conta de jogador</h1>
        <p style={styles.subtitle}>Cadastre-se para acessar saldo, pacotes e reservas.</p>
        <form onSubmit={submit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}
          <Field label="Unidade">
            <select value={form.tenant_slug} onChange={update('tenant_slug')} style={styles.input} required>
              <option value="">Selecione a unidade</option>
              {tenants.map(tenant => <option key={tenant.slug} value={tenant.slug}>{tenant.name}</option>)}
            </select>
          </Field>
          <Field label="Nome completo"><input style={styles.input} value={form.name} onChange={update('name')} required /></Field>
          <Field label="Apelido para login"><input style={styles.input} value={form.nickname} onChange={update('nickname')} required /></Field>
          <Field label="WhatsApp"><input style={styles.input} value={form.phone} onChange={update('phone')} inputMode="tel" placeholder="(00) 00000-0000" required /></Field>
          <Field label="E-mail (opcional)"><input style={styles.input} type="email" value={form.email} onChange={update('email')} /></Field>
          <div style={styles.row}>
            <Field label="Senha">
              <PasswordInput value={form.password} onChange={update('password')} show={showPassword} toggle={() => setShowPassword(!showPassword)} />
            </Field>
            <Field label="Confirmar senha">
              <PasswordInput value={form.password_confirmation} onChange={update('password_confirmation')} show={showPassword} toggle={() => setShowPassword(!showPassword)} />
            </Field>
          </div>
          <button style={styles.submit} disabled={loading}>{loading ? 'Criando conta...' : 'CRIAR CONTA'}</button>
        </form>
        <button style={styles.back} onClick={() => navigate(returnTo || `/cliente/${form.tenant_slug || requestedTenant}`)}>Já tenho conta — entrar</button>
        <p style={styles.security}>Este cadastro cria exclusivamente uma conta de jogador.</p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label style={styles.field}><span style={styles.label}>{label}</span>{children}</label>;
}

function PasswordInput({ value, onChange, show, toggle }) {
  return (
    <div style={styles.passwordWrap}>
      <input style={{ ...styles.input, paddingRight: 44 }} type={show ? 'text' : 'password'} value={value} onChange={onChange} minLength={6} required />
      <button type="button" style={styles.eyeBtn} onClick={toggle}>{show ? '🙈' : '👁️'}</button>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', padding: '28px 16px', boxSizing: 'border-box', background: 'radial-gradient(circle at 50% 25%, #14210a 0%, #0a0a0f 60%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },
  card: { background: '#14141f', border: '1px solid #262636', borderRadius: 16, padding: '30px 34px', width: 520, maxWidth: '92vw', boxSizing: 'border-box', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.5)' },
  logo: { width: 190, maxWidth: '75%', height: 'auto' },
  title: { color: '#fff', fontSize: 22, margin: '12px 0 6px' },
  subtitle: { color: '#6b7280', fontSize: 13, margin: '0 0 22px' },
  form: { textAlign: 'left' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'block', marginBottom: 13, minWidth: 0 },
  label: { display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 5, fontWeight: 600 },
  input: { width: '100%', padding: '10px 12px', boxSizing: 'border-box', background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none' },
  passwordWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: 6, top: 6, width: 32, height: 30, border: 0, borderRadius: 7, background: '#1a1a28', color: '#fff', cursor: 'pointer' },
  submit: { width: '100%', padding: 12, marginTop: 5, background: 'linear-gradient(135deg, var(--accent), #65a30d)', border: 0, borderRadius: 8, color: '#0a0a0f', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
  back: { width: '100%', padding: 11, marginTop: 12, background: 'transparent', border: '1px solid var(--accent)', borderRadius: 8, color: 'var(--accent)', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  error: { background: '#450a0a', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13 },
  security: { color: '#4b5563', fontSize: 11, margin: '16px 0 0' },
};
